/* eslint-disable complexity */
import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ActivatedRoute, ActivatedRouteSnapshot, Router, UrlTree } from '@angular/router';
import { ApplicationService } from '@app/store/application/application.service';
import { QuoteSessionQuery } from '@app/store/session/session.query';
import { QuoteSessionService } from '@app/store/session/session.service';
import { isModernLink } from '@app/utils/brand.util';
import { RETRIEVE_TYPES } from '@constants/api-endpoints';
import { API_ERRORS } from '@constants/api-errors';
import { DATADOG_EVENTS, DATADOG_PAGES } from '@constants/datadog';
import { ROUTES } from '@constants/routes';
import { USAA } from '@constants/usaa';
import { datadogRum } from '@datadog/browser-rum';
import { resetStores } from '@datorama/akita';
import { QuoteSession } from '@models/QuoteSession';
import { UtmParameters } from '@models/UtmParameters';
import { ExperimentationService } from '@services/experimentation.service';
import { LoggingService } from '@services/logging.service';
import { OrchestratorService } from '@services/orchestrator/orchestrator.service';
import { RetrieveCookieService } from '@services/retrieve-cookie.service';
import { UtmService } from '@services/utm.service';
import { firstValueFrom } from 'rxjs';
import { map } from 'rxjs/operators';


@Injectable({
	providedIn: 'root',
})
export class AppStartGuard  {
	constructor(
		private readonly router: Router,
		private readonly activatedRoute: ActivatedRoute,
		private readonly sessionState: QuoteSessionQuery,
		private readonly sessionActions: QuoteSessionService,
		private readonly orchestratorService: OrchestratorService,
		private readonly utmService: UtmService,
		private readonly appActions: ApplicationService,
		private readonly retrieveCookieService: RetrieveCookieService,
		private readonly loggingService: LoggingService,
		private readonly experimentationService: ExperimentationService
	) {}

	async canActivate(
		route: ActivatedRouteSnapshot,
	): Promise<boolean | UrlTree> {
		const hasActiveSession = await firstValueFrom(
			this.sessionState.sessionId$()
		);
		if (hasActiveSession) {
			await this.experimentationService.initializeClient();

			return this.router.parseUrl(ROUTES.SELECT_A_POLICY);
		}

		const retrieveTypes: string[] = [RETRIEVE_TYPES.WEB, RETRIEVE_TYPES.SMS];
		// if retrieveType is undefined, i.e. address is `/retrieve`, then set retrieveType to 'web'
		const retrieveTypeParam =
			this.activatedRoute.snapshot.params.retrieveType || RETRIEVE_TYPES.WEB;

		// if retrieveType is not 'sms' or 'web' throw error page
		if (!retrieveTypes.includes(retrieveTypeParam.toLowerCase())) {
			this.router.navigate([ROUTES.ERROR], {
				state: {
					detail: `This page does not exist`,
				},
			});
		}

		const { queryParams } = route;
		/** Get the brand that is stored in session */
		const sessionBrand = await firstValueFrom(this.sessionState.brand$());
		/** Gather UTM params that are used at the start of the app */
		const { orgcode, email, zip, zip_code, zipCode,
			senderId, code, coverage, landingPage, experimentation, variation } =
			queryParams;
		let { brand, orgCode, subProducerNumber } = queryParams;

		// store the original brand query string for retrieval use
		const brandQueryString: string = brand ?? '';

		/** delay start date options */

		const delayStartDate: number = parseFloat(route.queryParams.delayStartDate);
		/** set zip based on the many ways it can come in */
		const quoteZipCode: string = zip || zip_code || zipCode;
		/** If there is no `brand` query param, use `sessionBrand`, if neither it's `Embrace` */
		brand ??= sessionBrand || `Embrace`;
		/** Allow either spelling of `orgCode` */
		orgCode ??= orgcode;

		/** Delay startDate if needed */
		if (delayStartDate) {
			this.sessionActions.update({ delayPolicyStartDateDays: delayStartDate });
		}

		/** Handle UTM params */
		const utmValues: UtmParameters & { gclid: string } = {
			campaign: queryParams.utm_campaign ?? ``,
			channel: queryParams.utm_channel ?? ``,
			content: queryParams.utm_content ?? ``,
			medium: queryParams.utm_medium ?? ``,
			source: queryParams.utm_source ?? ``,
			term: queryParams.utm_term ?? ``,
			gclid: queryParams.gclid ?? ``,
		};

		this.utmService.setUtmValues(utmValues);
		this.sessionActions.updateUTMParams(this.utmService.getUtmCookie());

		/** Set partnerSenderId if there is one */
		this.sessionActions.updateContact({ partnerSenderId: senderId });

		if (brand) {
			/** Handle ModernLink brands */
			if (isModernLink(brand) && code) {
				try {
					const res = await firstValueFrom(
						this.orchestratorService.getOrgCodeFromSubProducer(code)
					);
					orgCode = res.orgCode;
					brand = res.brand;
					subProducerNumber = res.subProducerNumber;
				} catch (error) {
					this.loggingService.error(error);
				}
			}
			this.sessionActions.setBrandOnContact(brand);
		}

		if (
			!!brand &&
			!!sessionBrand &&
			brand.toLowerCase() !== sessionBrand.toLowerCase()
		) {
			resetStores();
			this.sessionActions.setBrandOnContact(brand);
		}

		/** Set orgcode on the contact */
		if (orgCode) {
			this.sessionActions.updateContact({ organizationCode: orgCode });
		}

		/** Set subProducerNumber on the contact */
		if (subProducerNumber) {
			this.sessionActions.update({ subProducerNumber });
			this.sessionActions.updateContact({ subProducerNumber });
		}
		const retrieveParam = route.queryParams.retrieve || '';
		const retrievalCookie = this.retrieveCookieService.getRetrieveCookie();

		// if we got the experimentation flag, set the value to the store
		if (experimentation) {
			this.appActions.setExperimentationEnabled(experimentation);
		}

		// if we got a param to force a specific variation of a test,
		// set the value to the store
		if (variation) {
			this.appActions.setForcedExperimentVariation(variation);
		}

		if (email && quoteZipCode) {
			this.sessionActions.reset();
			this.sessionActions.updateContact({
				emailAddress: email,
				ratingZipCode: quoteZipCode,
			});

			this.appActions.setLoading(true, undefined, undefined, 'retrieve');

			if (coverage) {
				await this.retrieveQuote(
					email,
					quoteZipCode,
					brandQueryString,
					RETRIEVE_TYPES.QEV3,
					coverage,
				);
			} else if (retrieveParam && retrieveParam === 'embed' && retrievalCookie) {
				// if we are coming from an embedded widget, we need to retrieve based
				// on the contact id saved in the retrieve cookie
				await this.retrieveQuoteByContactId(retrievalCookie);
			} else if (landingPage) {
				await this.retrieveQuote(
					email as string,
					quoteZipCode,
					brandQueryString,
					RETRIEVE_TYPES.PAGE_3,
				);
			} else {
				await this.retrieveQuote(email, quoteZipCode, brandQueryString);
			}

			this.appActions.setLoading(false, undefined, undefined, 'retrieve');

			// if we have a landingPage param, and it's 3, go to /contact-info
			if (landingPage && landingPage === '3') {
				datadogRum.addAction(DATADOG_EVENTS.LANDING_PAGE, {
					page: DATADOG_PAGES.CONTACT_INFO,
					isCookieRetrieve: false
				});

				await this.experimentationService.initializeClient();

				return this.router.navigate([ROUTES.CONTACT_INFORMATION]);
			}

			datadogRum.addAction(DATADOG_EVENTS.LANDING_PAGE, {
				page: DATADOG_PAGES.SELECT_POLICY,
				isCookieRetrieve: false
			});

			await this.experimentationService.initializeClient();

			// if it is an embedded form retrieve, hide the message
			return this.router.navigate([ROUTES.SELECT_A_POLICY]);
		}

		// for retrieves coming from the website, get by contact id
		// and show retrieve quote banner
		if (retrievalCookie && retrieveParam === 'w') {
			this.sessionActions.reset();

			this.appActions.setLoading(true, undefined, undefined, 'retrieve');
			await this.retrieveQuoteByContactId(retrievalCookie);

			this.appActions.setLoading(false, undefined, undefined, 'retrieve');

			datadogRum.addAction(DATADOG_EVENTS.LANDING_PAGE, {
				page: DATADOG_PAGES.SELECT_POLICY,
				isCookieRetrieve: true
			});

			await this.experimentationService.initializeClient();

			return this.router.navigate([ROUTES.SELECT_A_POLICY]);
		}

		return true;
	}

	private async retrieveQuoteByContactId(contactId: string): Promise<void> {
		try {
			const res: QuoteSession = await firstValueFrom(
				this.orchestratorService
					.retrieveQuote({
						contactId,
						retrieveType: RETRIEVE_TYPES.WEB,
					})
			);
			this.sessionActions.update(res);
			this.appActions.setLastPetActive();
		} catch (error) {
			const err = error as HttpErrorResponse;
			if (err.error) {
				err.error.title ??= ``;
				this.appActions.setLoading(false);
				switch (err.error.title) {
					case API_ERRORS.POLICY_HOLDER_EXISTS:
						this.router.navigate([ROUTES.ERROR], {
							state: {
								detail: err.error.detail?.split(` :: `)[1],
							},
						});
						break;
					case API_ERRORS.QUOTE_RETRIEVAL_FAILED:
						this.retrieveCookieService.delete();
						this.router.navigate([ROUTES.PET_INFORMATION], {
							queryParams: {
								rq: '4',
							},
							state: {
								quoteRetrievalError: true,
							},
							queryParamsHandling: `merge`,
						});
						break;
					default:
						this.router.navigate([ROUTES.ERROR], {
							state: {
								// eslint-disable-next-line max-len
								detail: `There was an error retrieving your quote. Please try again shortly. If you continue to have issues please contact us.`,
							},
						});
						break;
				}
			}

			return;
		}
	}

	private async retrieveQuote(
		emailAddress: string,
		ratingZipCode: string,
		brand: string,
		retrieveType: string = RETRIEVE_TYPES.QEV3,
		coverage: string = '',
		isWebRetrieve: boolean = false
	): Promise<void> {
		const analyticsInfo = this.utmService.getUtmCookie();
		try {
			const res: QuoteSession = await firstValueFrom(
				this.orchestratorService
					.retrieveQuote({
						emailAddress,
						ratingZipCode,
						isWebRetrieve,
						analyticsInfo,
						brand,
						retrieveType,
					})
					.pipe(
						map((r) =>
							coverage
								? firstValueFrom(
									this.orchestratorService.updatePetCoverage(
										r.id,
										coverage,
										r.pets[0]
									)
								  )
								: r
						)
					)
			);
			this.sessionActions.update(res);

			// on retrieve, we need to check for USAA SSO Inactive flow trait
			// and set the flag that will give the same experience on page 3
			// that they had the first time.
			const { quoteDetails } = res || {};
			const { traits } = quoteDetails || {};

			if (traits?.length && traits.includes(USAA.SSO_INACTIVE)) {
				this.appActions.setUsaaSsoInactive(true);
			}
			this.appActions.setLastPetActive();

			return;
		} catch (error) {
			const err = error as HttpErrorResponse;
			if (err.error) {
				err.error.title ??= ``;
				this.appActions.setLoading(false);
				switch (err.error.title) {
					case API_ERRORS.POLICY_HOLDER_EXISTS:
						this.router.navigate([ROUTES.ERROR], {
							state: {
								detail: err.error.detail?.split(` :: `)[1],
							},
						});
						break;
					case API_ERRORS.QUOTE_RETRIEVAL_FAILED:
						this.router.navigate([ROUTES.PET_INFORMATION], {
							queryParams: {
								rq: '4',
								brand
							},
							state: {
								quoteRetrievalError: true,
							},
							queryParamsHandling: `merge`,
						});
						break;
					default:
						this.router.navigate([ROUTES.ERROR], {
							state: {
								// eslint-disable-next-line max-len
								detail: `There was an error retrieving your quote. Please try again shortly. If you continue to have issues please contact us.`,
							},
						});
						break;
				}
			}

			return;
		}
	}
}
